import { call, put, takeLatest, select, take, delay } from 'redux-saga/effects';
import {
  selectErpSystem,
  selectLanguage,
} from 'app/features/erpSystem/slice/selectors';
import { configuratorDataActions as actions } from '.';
import { ApiResponseData, FilterAttributesType, SingleItemData } from './types';
import {
  selectPaginationLimit,
  selectPaginationSkip,
} from 'app/features/pagination/slice/selectors';
import {
  getDiffPressureKeyData,
  transformDTO,
  transformFilterAttributesDTO,
} from './utils';
import { selectStep } from 'app/features/step/slice/selectors';
import { selectSelectedFilters } from 'app/features/selectedFilters/slice/selectors';
import { configuratorSteps } from 'utils/configuratorSteps';
import { selectSearch } from 'app/features/search/slice/selectors';
import { priceActions } from '../../price/slice/index';
import { stockActions } from '../../stock/slice/index';
import { selectPriceResponse } from 'app/features/price/slice/selectors';
import { selectStockResponse } from 'app/features/stock/slice/selectors';

import {
  selectSavedItem,
  selectSelectedItem,
} from 'app/features/selectedItem/slice/selectors';
import {
  selectMetadataFilterAttr,
  selectMetadataMandatoryAttr,
} from 'app/features/metadata/slice/selectors';
import { getRequestPayload } from './requestPayload';

export function* fetchConfiguratorData() {
  const {
    getConfiguratorDataSuccess,
    getConfiguratorDataFail,
    setDisplaySafetyFactor,
    setMountingApply,
  } = actions;
  const { getPriceData } = priceActions;
  const { getStockStatus } = stockActions;
  let selectedItem: SingleItemData | null = null;
  let couplingMandatoryAttr: Array<string> = [];
  let bracketMandatoryAttr: Array<string> = [];

  try {
    const language = yield select(selectLanguage);
    const limit = yield select(selectPaginationLimit);
    const skip = yield select(selectPaginationSkip);
    const currentStep = yield select(selectStep);
    const selectFilterAttributes = yield select(selectSelectedFilters);
    const query = yield select(selectSearch);
    const erpSystem = yield select(selectErpSystem);
    const savedItemValues = yield select(selectSelectedItem);
    const isGearboxApplicable = savedItemValues[1].item?.isGearboxApplicable
      ? true
      : false;
    const isNonDirectMount = savedItemValues[1].item?.isNonDirectMount
      ? true
      : false;
    // Need to fetch basic form based on the selection or current step needs to be passed

    const mandatoryItemAttr = yield select(selectMetadataMandatoryAttr, {
      basicForm:
        currentStep === 3
          ? selectFilterAttributes[currentStep].filter.attributes.filter(
              attribute =>
                attribute.attributeIdentifier === 'EGTF_3734__STRING',
            )[0].values[0]
          : selectFilterAttributes[currentStep].filter.basicForm,
      actuatorType: selectFilterAttributes[2].filter.basicForm,
      valveType: selectFilterAttributes[1].filter.basicForm,
      currentStep: currentStep,
      isNonDirectMount: isNonDirectMount,
    });

    if (isNonDirectMount && currentStep === 2) {
      couplingMandatoryAttr = yield select(selectMetadataMandatoryAttr, {
        basicForm: 'coupling',
        actuatorType: selectFilterAttributes[2].filter.basicForm,
        valveType: selectFilterAttributes[1].filter.basicForm,
        currentStep: currentStep,
        isNonDirectMount: isNonDirectMount,
      });
      bracketMandatoryAttr = yield select(selectMetadataMandatoryAttr, {
        basicForm: 'bracket',
        actuatorType: selectFilterAttributes[2].filter.basicForm,
        valveType: selectFilterAttributes[1].filter.basicForm,
        currentStep: currentStep,
        isNonDirectMount: isNonDirectMount,
      });
    }

    const metadataFilterAttr = yield select(selectMetadataFilterAttr, {
      basicForm: selectFilterAttributes[currentStep].filter.basicForm,
      valveType: selectFilterAttributes[1].filter.basicForm,
    });
    const differentialPressAttr: Array<string> =
      metadataFilterAttr.Differential_Pressure;

    if (currentStep > 1 && currentStep < 4) {
      selectedItem = yield select(state =>
        selectSavedItem(state, currentStep - 1),
      );
    } else if (currentStep === 4) {
      selectedItem = yield select(state =>
        selectSavedItem(state, currentStep - 2),
      );
    }
    const payload = getRequestPayload(
      erpSystem,
      language,
      limit,
      skip,
      query,
      selectFilterAttributes,
      currentStep,
      selectedItem,
      differentialPressAttr,
      mandatoryItemAttr,
      isNonDirectMount,
      couplingMandatoryAttr,
      bracketMandatoryAttr,
    );

    let configuratorData;
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      configuratorData = yield call(
        fetch,
        `/valve-configurator/api/${configuratorSteps[currentStep - 1].name}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (configuratorData.status === 200) {
        break;
      }

      attempts++;
      console.log(`Valve retry attempt: ${attempts}`);
      yield delay(300);
    }

    if (!configuratorData || configuratorData.status !== 200) {
      return yield put(getConfiguratorDataFail('Valve failed after retries'));
    } else {
      const data: ApiResponseData = yield configuratorData.json();

      if (!data) {
        return yield put(getConfiguratorDataFail('No items fetched'));
      }

      const filterAttributesTransformed: FilterAttributesType =
        selectFilterAttributes[currentStep].filter.basicForm === 'gearbox' ||
        currentStep !== 2
          ? yield transformFilterAttributesDTO(
              data.filterAttributes,
              currentStep,
              selectFilterAttributes,
              isGearboxApplicable,
            )
          : yield getDiffPressureKeyData(
              selectedItem?.attributes,
              differentialPressAttr,
              currentStep,
              selectFilterAttributes,
              data.filterAttributes,
              isGearboxApplicable,
            );

      let itemsPriceData: any = {};
      let itemsStockData: any = {};

      if (data.items.length) {
        yield put(getPriceData(data.items));
        yield take(['price/getPriceDataSuccess', 'price/getPriceDataFail']); // wait for price call to either succeed or fail
        itemsPriceData = yield select(selectPriceResponse);

        yield put(getStockStatus(data.items));
        yield take(['stock/getStockStatusSuccess', 'stock/getStockStatusFail']); // wait for stock status call to either succeed or fail
        itemsStockData = yield select(selectStockResponse);
      }

      const itemsTransformed = yield transformDTO(
        data.items,
        itemsPriceData || [],
        itemsStockData || [],
      );

      if (data.displaySafetyFactor) {
        yield put(setDisplaySafetyFactor(data.displaySafetyFactor));
      }
      if (data.mountingApply !== undefined) {
        yield put(setMountingApply(data.mountingApply));
      }

      return yield put(
        getConfiguratorDataSuccess({
          ...data,
          ...itemsTransformed,
          ...filterAttributesTransformed,
        }),
      );
    }
  } catch (e) {
    yield put(getConfiguratorDataFail(e));
  }
}
export function* configuratorDataSaga() {
  yield takeLatest(actions.getConfiguratorData.type, fetchConfiguratorData);
}
